'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useNotifications } from '@/contexts/NotificationContext';
import toast from 'react-hot-toast';
import KpiStrip from './KpiStrip';
import type { LogisticsJob, Alert } from './types';
import type { FilterKey, SortKey } from './types';
import PermitChip from './PermitChip';
import ReadinessRing from './ReadinessRing';
import SlaCountdown from './SlaCountdown';
import QueueControls from './QueueControls';
import LiveRadarMap from '@/app/logistics-supply-chain-dashboard/components/LiveRadarMap';
import DumpsterConfirmDialog from './DumpsterConfirmDialog';
import LogIncidentModal from './LogIncidentModal';
import QuickActionsFab from '@/app/logistics-supply-chain-dashboard/components/QuickActionsFab';
import type { DumpsterAction } from './types';
import IdentifyJurisdictionButton, { type JurisdictionResult } from '@/app/permits/components/IdentifyJurisdictionButton';
import Link from 'next/link';
import CreateJobModal from './CreateJobModal';
import AISummaryBar from '@/components/common/AISummaryBar';
import ScheduleInspectionModal from './ScheduleInspectionModal';
import SupabaseStatusDot from '@/components/common/SupabaseStatusDot';
// ... existing code ...
// ─── Default fallback coordinates (Dayton, OH — HQ market) ───────────────────
const DEFAULT_COORDS = { lat: 39.7589, lng: -84.1916 }; // Dayton, OH

// ─── appendNoteEntry helper ───────────────────────────────────────────────────
const appendNoteEntry = (existingNotes: string | null | undefined, action: string, actor: string): string => {
  const now = new Date();
  const stamp = now.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
  const entry = `[${stamp}] ${action} — ${actor}`;
  if (!existingNotes || existingNotes.trim().length === 0) return entry;
  return `${entry}\n${existingNotes}`;
};

// ─── Types ────────────────────────────────────────────────────────────────────
// Interfaces moved to ./types.ts — imported above

// ─── Initials helper ──────────────────────────────────────────────────────────

const getInitials = (fullName: string): string => {
  if (!fullName) return '??';
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || fullName.slice(0, 2).toUpperCase();
};

// ─── Queue filter/sort/search helpers ────────────────────────────────────────

function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function safeParseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  try {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function isJobComplete(job: LogisticsJob): boolean {
  const materialsDone = job.materials_status === 'Delivered';
  const dumpsterDone = job.dumpster_status === 'Removed';
  const permitDone = job.permit_status === 'passed' || job.permit_status === 'closed';
  return materialsDone && dumpsterDone && permitDone;
}

function getHomeownerName(job: LogisticsJob): string {
  return job.contact_directory?.homeowner?.name ?? '';
}

// ─── Status helpers ────────────────────────────────────────────────────────────

const MATERIALS_STATUS_CONFIG: Record<string, { bg: string; text: string; dot: string }> = {
  'Pending':   { bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400' },
  'Ordered':   { bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  'En Route':  { bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  'Loaded':    { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  'Delivered': { bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500' },
};

const DUMPSTER_STATUS_CONFIG: Record<string, { bg: string; text: string; dot: string }> = {
  'Scheduled': { bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500' },
  'Placed':    { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  'Removed':   { bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400' },
};

const MATERIAL_ITEM_STATUS: Record<string, { bg: string; text: string }> = {
  'Pending':   { bg: 'bg-gray-100',   text: 'text-gray-600' },
  'Ordered':   { bg: 'bg-blue-100',   text: 'text-blue-700' },
  'Loaded':    { bg: 'bg-purple-100', text: 'text-purple-700' },
  'Delivered': { bg: 'bg-green-100',  text: 'text-green-700' },
};

const getMaterialsConfig = (s: string) => MATERIALS_STATUS_CONFIG[s] ?? MATERIALS_STATUS_CONFIG['Pending'];
const getDumpsterConfig   = (s: string) => DUMPSTER_STATUS_CONFIG[s]  ?? DUMPSTER_STATUS_CONFIG['Scheduled'];
const getMaterialItemConfig = (s: string) => MATERIAL_ITEM_STATUS[s]  ?? MATERIAL_ITEM_STATUS['Pending'];

const formatDate = (d: string) => {
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

// ─── Radar Map Visual ─────────────────────────────────────────────────────────

// Removed static RadarMapVisual — replaced by LiveRadarMap component

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LogisticsDashboardInteractive() {
  const { currentUser } = useAuth();
  const [jobs, setJobs] = useState<LogisticsJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [clockedIn, setClockedIn] = useState(false);
  const [selectedJob, setSelectedJob] = useState<LogisticsJob | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [alertsBellPulse, setAlertsBellPulse] = useState(true);
  const [materialStatuses, setMaterialStatuses] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sortKey, setSortKey] = useState<SortKey>('install_date_asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [resolveModalJob, setResolveModalJob] = useState<{ job: LogisticsJob; alert: Alert } | null>(null);
  const [resolveActionLoading, setResolveActionLoading] = useState<string | null>(null);
  const [logIncidentOpen, setLogIncidentOpen] = useState(false);
  const [dumpsterAction, setDumpsterAction] = useState<DumpsterAction | null>(null);
  const [createJobOpen, setCreateJobOpen] = useState(false);
  const [scheduleInspectionOpen, setScheduleInspectionOpen] = useState(false);
  const [drawerEscalateLoading, setDrawerEscalateLoading] = useState(false);
  // Escalation description modal state
  const [escalateDescModal, setEscalateDescModal] = useState<{ type: 'alert'; alert: any } | { type: 'drawer'; job: LogisticsJob } | null>(null);
  const [escalateDesc, setEscalateDesc] = useState('');
  const [escalateDepts, setEscalateDepts] = useState<string[]>([]);
  const drawerRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();
  const { notifyNewEscalation } = useNotifications();

  // Permits state for drawer
  const [permitsByJobId, setPermitsByJobId] = useState<Record<string, { id: string; job_id: string; status: string; jurisdiction_name: string | null; permit_portal_url: string | null }>>({});

  // Count total alerts
  const totalAlerts = jobs.reduce((acc, j) => acc + (j.alerts?.length ?? 0), 0);
  const weatherAlerts = jobs.filter(j => j.alerts?.some(a => a.type === 'weather'));
  const supplyAlerts  = jobs.filter(j => j.alerts?.some(a => a.type === 'supply'));

  // ─── Processed jobs (filter + search + sort) ────────────────────────────────
  const processedJobs = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const weekStart = getStartOfWeek(now);
    const weekEnd = addDays(weekStart, 6);
    const nextWeekStart = addDays(weekStart, 7);
    const nextWeekEnd = addDays(nextWeekStart, 6);

    let result = jobs.filter(job => {
      switch (filter) {
        case 'all':
          return true;
        case 'at_risk':
          return job.risk_flag === true || (job.alerts ?? []).some(a => a.severity === 'high');
        case 'this_week': {
          const d = safeParseDate(job.installation_date);
          if (!d) return false;
          return d >= weekStart && d <= weekEnd;
        }
        case 'next_week': {
          const d = safeParseDate(job.installation_date);
          if (!d) return false;
          return d >= nextWeekStart && d <= nextWeekEnd;
        }
        case 'unscheduled':
          return !safeParseDate(job.installation_date);
        case 'complete':
          return isJobComplete(job);
        default:
          return true;
      }
    });

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(job => {
        const haystack = [
          job.job_id,
          job.address,
          getHomeownerName(job),
          job.vpm_name ?? '',
          job.lead_installer_name ?? '',
        ].join(' ').toLowerCase();
        return haystack.includes(q);
      });
    }

    const sorted = [...result];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'install_date_asc': {
          const da = safeParseDate(a.installation_date);
          const db = safeParseDate(b.installation_date);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return da.getTime() - db.getTime();
        }
        case 'install_date_desc': {
          const da = safeParseDate(a.installation_date);
          const db = safeParseDate(b.installation_date);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return db.getTime() - da.getTime();
        }
        case 'readiness_desc':
          return (b.readiness_score ?? 0) - (a.readiness_score ?? 0);
        case 'readiness_asc':
          return (a.readiness_score ?? 0) - (b.readiness_score ?? 0);
        case 'vpm':
          return (a.vpm_name ?? '').localeCompare(b.vpm_name ?? '');
        case 'crew':
          return (a.lead_installer_name ?? '').localeCompare(b.lead_installer_name ?? '');
        default:
          return 0;
      }
    });

    return sorted;
  }, [jobs, filter, sortKey, searchQuery]);

  // ─── Filter counts ───────────────────────────────────────────────────────────
  const filterCounts = useMemo<Record<FilterKey, number>>(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const weekStart = getStartOfWeek(now);
    const weekEnd = addDays(weekStart, 6);
    const nextWeekStart = addDays(weekStart, 7);
    const nextWeekEnd = addDays(nextWeekStart, 6);

    return {
      all: jobs.length,
      at_risk: jobs.filter(j => j.risk_flag === true || (j.alerts ?? []).some(a => a.severity === 'high')).length,
      this_week: jobs.filter(j => {
        const d = safeParseDate(j.installation_date);
        return d !== null && d >= weekStart && d <= weekEnd;
      }).length,
      next_week: jobs.filter(j => {
        const d = safeParseDate(j.installation_date);
        return d !== null && d >= nextWeekStart && d <= nextWeekEnd;
      }).length,
      unscheduled: jobs.filter(j => !safeParseDate(j.installation_date)).length,
      complete: jobs.filter(isJobComplete).length,
    };
  }, [jobs]);

  const loadJobs = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('logistics_jobs')
        .select('*')
        .order('installation_date', { ascending: true });
      if (error) throw error;
      setJobs(data ?? []);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();

    const channel = supabase
      .channel('logistics_jobs_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'logistics_jobs' }, () => {
        loadJobs();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [loadJobs]);

  // Load permits for drawer permit section
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('permits').select('id, job_id, status, jurisdiction_name, permit_portal_url');
      const map: Record<string, { id: string; job_id: string; status: string; jurisdiction_name: string | null; permit_portal_url: string | null }> = {};
      (data ?? []).forEach((p: { id: string; job_id: string; status: string; jurisdiction_name: string | null; permit_portal_url: string | null }) => { map[p.job_id] = p; });
      setPermitsByJobId(map);
    };
    load();
    const channel = supabase
      .channel('permits_realtime_for_drawer')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'permits' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [supabase]);

  const openDrawer = (job: LogisticsJob) => {
    setSelectedJob(job);
    // Initialize material statuses from job data
    const statuses: Record<string, string> = {};
    job.material_order?.forEach((item, idx) => {
      statuses[`${job.id}-${idx}`] = item.status;
    });
    setMaterialStatuses(prev => ({ ...prev, ...statuses }));
    setDrawerOpen(true);
  };

  const handleCreatePermitFromAI = async (job: LogisticsJob, result: JurisdictionResult) => {
    const pb = result.playbook;
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const now = new Date().toISOString();

    const parseFee = (formula: string | null): number | null => {
      if (!formula) return null;
      const match = formula.match(/\$([0-9,]+(?:\.[0-9]{1,2})?)/);
      if (!match) return null;
      const n = Number(match[1].replace(/,/g, ''));
      return isNaN(n) ? null : n;
    };

    const insertPayload = {
      job_id: job.job_id,
      jurisdiction_playbook_id: pb.id,
      jurisdiction_name: pb.jurisdiction_name,
      jurisdiction_type: pb.jurisdiction_type,
      status: 'needs_filing',
      permit_portal_url: pb.permit_portal_url ?? null,
      required_documents: pb.required_documents ?? [],
      fee_amount: parseFee(pb.fee_formula_text),
      assigned_to_name: actorName || 'Unassigned',
      assigned_to_id: currentUser?.id ?? null,
      notes: pb.special_notes ?? null,
      timeline_log: [{
        action: 'permit_created_via_ai',
        actor: actorName,
        actor_id: currentUser?.id ?? null,
        timestamp: now,
        note: `${result.source === 'cache' ? 'Cache hit' : 'AI-identified'}: ${pb.jurisdiction_name} (confidence ${Math.round((pb.confidence_score ?? 0) * 100)}%)`,
      }],
    };

    try {
      const { error } = await supabase.from('permits').insert(insertPayload);
      if (error) throw error;
      toast.success(`Permit started for ${job.job_id} in ${pb.jurisdiction_name}`, { duration: 4000, icon: '📋' });
    } catch (err) {
      console.error('Failed to create permit:', err);
      toast.error(`Failed to create permit: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedJob(null), 300);
  };

  const handleMarkDelivered = async (job: LogisticsJob) => {
    try {
      const { error } = await supabase
        .from('logistics_jobs')
        .update({ materials_status: 'Delivered', updated_at: new Date().toISOString() })
        .eq('id', job.id);
      if (error) throw error;
      toast.success(`${job.job_id} marked as delivered`);
    } catch (err) {
      toast.error(`Failed to update ${job.job_id}`);
      console.error('Mark delivered error:', err);
    }
  };

  // ─── Resolve Modal Handlers ───────────────────────────────────────────────

  const handleSubstituteSKU = async (alert: any) => {
    setResolveActionLoading('substitute');
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const actionText = `Substitute SKU initiated for "${alert.material_description}"`;
    const newNotes = appendNoteEntry(alert.notes, actionText, actorName);

    // Remove this supply alert from the job's alerts array and update materials_status
    const updatedAlerts = (alert.currentAlerts ?? []).filter((_: any, idx: number) => idx !== alert.alertIndex);
    const updatePayload = {
      materials_status: 'Ordered',
      alerts: updatedAlerts,
      updated_at: new Date().toISOString(),
    };

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Update timed out')), 10000)
    );
    const savePromise = supabase
      .from('logistics_jobs')
      .update(updatePayload)
      .eq('id', alert.jobUUID)
      .then(res => { if (res.error) throw res.error; return res; });

    // Also try to update materials_alerts if a matching row exists (best-effort)
    supabase.from('materials_alerts').update({
      delivery_status: 'substitute',
      notes: newNotes,
      updated_at: new Date().toISOString(),
    }).eq('job_id', alert.job_id).then(() => {});

    try {
      await Promise.race([savePromise, timeoutPromise]);
      toast.success(`Substitute SKU logged for ${alert.job_id}`, { duration: 3500, icon: '🔁' });
      setResolveModalJob(null);
    } catch (err) {
      toast.error(`Substitute SKU failed: ${err instanceof Error ? err.message : 'Unknown'}`);
    } finally {
      setResolveActionLoading(null);
    }
  };

  const handleRushShip = async (alert: any) => {
    setResolveActionLoading('rush');
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const actionText = `Rush ship requested from ${alert.supplier_name} for "${alert.material_description}"`;
    const newNotes = appendNoteEntry(alert.notes, actionText, actorName);

    const updatedAlerts = (alert.currentAlerts ?? []).filter((_: any, idx: number) => idx !== alert.alertIndex);
    const updatePayload = {
      materials_status: 'En Route',
      alerts: updatedAlerts,
      updated_at: new Date().toISOString(),
    };

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Update timed out')), 10000)
    );
    const savePromise = supabase
      .from('logistics_jobs')
      .update(updatePayload)
      .eq('id', alert.jobUUID)
      .then(res => { if (res.error) throw res.error; return res; });

    supabase.from('materials_alerts').update({
      delivery_status: 'rush',
      notes: newNotes,
      updated_at: new Date().toISOString(),
    }).eq('job_id', alert.job_id).then(() => {});

    try {
      await Promise.race([savePromise, timeoutPromise]);
      toast.success(`Rush ship requested for ${alert.job_id}`, { duration: 3500, icon: '🚚' });
      setResolveModalJob(null);
    } catch (err) {
      toast.error(`Rush ship failed: ${err instanceof Error ? err.message : 'Unknown'}`);
    } finally {
      setResolveActionLoading(null);
    }
  };

  const handleSplitShip = async (alert: any) => {
    setResolveActionLoading('split');
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const actionText = `Split shipment arranged with ${alert.supplier_name} — partial delivery now, balance to follow`;
    const newNotes = appendNoteEntry(alert.notes, actionText, actorName);

    const updatedAlerts = (alert.currentAlerts ?? []).filter((_: any, idx: number) => idx !== alert.alertIndex);
    const updatePayload = {
      materials_status: 'Loaded',
      alerts: updatedAlerts,
      updated_at: new Date().toISOString(),
    };

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Update timed out')), 10000)
    );
    const savePromise = supabase
      .from('logistics_jobs')
      .update(updatePayload)
      .eq('id', alert.jobUUID)
      .then(res => { if (res.error) throw res.error; return res; });

    supabase.from('materials_alerts').update({
      delivery_status: 'split',
      notes: newNotes,
      updated_at: new Date().toISOString(),
    }).eq('job_id', alert.job_id).then(() => {});

    try {
      await Promise.race([savePromise, timeoutPromise]);
      toast.success(`Split shipment logged for ${alert.job_id}`, { duration: 3500, icon: '📦' });
      setResolveModalJob(null);
    } catch (err) {
      toast.error(`Split shipment failed: ${err instanceof Error ? err.message : 'Unknown'}`);
    } finally {
      setResolveActionLoading(null);
    }
  };

  const handleContactSupplier = async (alert: any) => {
    setResolveActionLoading('contact');
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const actionText = `Contacted ${alert.supplier_name} regarding "${alert.material_description}"`;
    const newNotes = appendNoteEntry(alert.notes, actionText, actorName);

    // Contact Supplier: don't change materials_status, just remove the alert
    const updatedAlerts = (alert.currentAlerts ?? []).filter((_: any, idx: number) => idx !== alert.alertIndex);
    const updatePayload = {
      alerts: updatedAlerts,
      updated_at: new Date().toISOString(),
    };

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Update timed out')), 10000)
    );
    const savePromise = supabase
      .from('logistics_jobs')
      .update(updatePayload)
      .eq('id', alert.jobUUID)
      .then(res => { if (res.error) throw res.error; return res; });

    supabase.from('materials_alerts').update({
      delivery_status: 'contacted',
      notes: newNotes,
      updated_at: new Date().toISOString(),
    }).eq('job_id', alert.job_id).then(() => {});

    try {
      await Promise.race([savePromise, timeoutPromise]);
      toast.success(`Contact logged for ${alert.supplier_name}`, { duration: 3500, icon: '📞' });
      setResolveModalJob(null);
    } catch (err) {
      toast.error(`Contact log failed: ${err instanceof Error ? err.message : 'Unknown'}`);
    } finally {
      setResolveActionLoading(null);
    }
  };

  const handleEscalate = async (alert: any, userDescription: string, departments: string[]) => {
    setResolveActionLoading('escalate');
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const actorId = currentUser?.id ?? '';
    const now = new Date().toISOString();

    const deptList = departments.length > 0 ? departments : ['Operations'];
    const baseDescription = `${userDescription ? userDescription + '\n\n' : ''}Material backorder/delay escalated from Logistics dashboard.\n\nMaterial: ${alert.material_description}\nSupplier: ${alert.supplier_name}\nExpected delivery: ${alert.expected_delivery_date || '(unknown)'}\n\nExisting notes:\n${alert.notes || '(none)'}`;

    const updatedAlerts = (alert.currentAlerts ?? []).filter((_: any, idx: number) => idx !== alert.alertIndex);
    const alertUpdatePayload = { alerts: updatedAlerts, updated_at: now };

    try {
      const insertedTicketIds: string[] = [];

      for (const dept of deptList) {
        const ticketId = `ESC-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        const escalationPayload = {
          ticket_id: ticketId,
          title: `Material Delay — ${alert.job_id}`,
          description: `${baseDescription}\n\nEscalated to: ${deptList.join(', ')}`,
          source_department: dept,
          priority: 'high',
          status: 'open',
          reported_by_id: actorId || null,
          reported_by_name: actorName || null,
          affected_job_id: alert.job_id ?? null,
          affected_job_address: alert.job_address ?? null,
          contact_directory: {},
          assignment_history: [],
          timeline_log: [{
            action: 'escalated_from_supplier_alert',
            actor: actorName,
            actor_id: actorId,
            timestamp: now,
            note: `Escalated from materials alert: ${alert.material_description}. Sent to: ${deptList.join(', ')}`,
          }],
        };
        const escResult = await Promise.race([
          supabase.from('escalations').insert(escalationPayload).select('id').single(),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Escalation insert timed out after 15s')), 15000)),
        ]);
        if ((escResult as any).error) throw (escResult as any).error;
        insertedTicketIds.push(ticketId);
      }

      // Step 2: Remove alert from logistics_jobs
      const { error: jobErr } = await supabase
        .from('logistics_jobs')
        .update(alertUpdatePayload)
        .eq('id', alert.jobUUID);
      if (jobErr) throw jobErr;

      // Step 3: Best-effort flag on materials_alerts
      await supabase.from('materials_alerts').update({
        delivery_status: 'escalated',
        notes: appendNoteEntry(alert.notes, `ESCALATED to ${deptList.join(', ')} — tickets ${insertedTicketIds.join(', ')}`, actorName),
        updated_at: now,
      }).eq('job_id', alert.job_id);

      toast.success(`Escalated to ${deptList.join(', ')} — ${insertedTicketIds.length} ticket(s) created`, { duration: 5000, icon: '🚨' });
      notifyNewEscalation(insertedTicketIds[0], `Material Delay — ${alert.job_id}`, alert.job_id ?? null, 'high', 'Logistics');
      setResolveModalJob(null);
    } catch (err: any) {
      const msg = err?.message ?? err?.details ?? err?.hint ?? 'Unknown error';
      toast.error(`Escalation failed: ${msg}`, { duration: 8000 });
    } finally {
      setResolveActionLoading(null);
    }
  };

  const handleDrawerEscalate = async (job: LogisticsJob, userDescription: string, departments: string[]) => {
    setDrawerEscalateLoading(true);
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const actorId = currentUser?.id ?? '';
    const now = new Date().toISOString();

    const deptList = departments.length > 0 ? departments : ['Operations'];
    const alertSummary = (job.alerts ?? [])
      .map((a: any) => `• ${a.message ?? a.type ?? 'Alert'}`)
      .join('\n') || '(no active alerts)';
    const baseDescription = `${userDescription ? userDescription + '\n\n' : ''}Issue escalated from job detail drawer on Logistics dashboard.\n\nJob: ${job.job_id}\nAddress: ${job.address}\nInstall Date: ${job.installation_date ?? '(unknown)'}\n\nActive Alerts:\n${alertSummary}`;

    try {
      const insertedTicketIds: string[] = [];

      for (const dept of deptList) {
        const ticketId = `ESC-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        const escalationPayload = {
          ticket_id: ticketId,
          title: `Job Issue Escalated — ${job.job_id}`,
          description: `${baseDescription}\n\nEscalated to: ${deptList.join(', ')}`,
          source_department: dept,
          priority: 'high',
          status: 'open',
          reported_by_id: actorId || null,
          reported_by_name: actorName || null,
          affected_job_id: job.job_id ?? null,
          affected_job_address: job.address ?? null,
          contact_directory: {},
          assignment_history: [],
          timeline_log: [{
            action: 'escalated_from_job_drawer',
            actor: actorName,
            actor_id: actorId,
            timestamp: now,
            note: `Escalated from job detail drawer for ${job.job_id}. Sent to: ${deptList.join(', ')}`,
          }],
        };
        let result = await Promise.race([
          supabase.from('escalations').insert(escalationPayload).select('id').single(),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Escalation timed out after 15s')), 15000)),
        ]);
        if ((result as any).error) throw (result as any).error;
        insertedTicketIds.push(ticketId);
      }

      toast.success(`Escalated to ${deptList.join(', ')} — ${insertedTicketIds.length} ticket(s) created`, { duration: 5000, icon: '🚨' });
      notifyNewEscalation(insertedTicketIds[0], `Job Issue Escalated — ${job.job_id}`, job.job_id ?? null, 'high', 'Logistics');
      closeDrawer();
    } catch (err: any) {
      const msg = err?.message ?? err?.details ?? err?.hint ?? 'Unknown error';
      toast.error(`Escalation failed: ${msg}`, { duration: 8000 });
    } finally {
      setDrawerEscalateLoading(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        closeDrawer();
      }
    };
    if (drawerOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [drawerOpen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#F8F9FA' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-4 border-[#E89A3C] border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500">Loading Logistics Dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>

      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <div className="sticky top-[60px] z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#1C2E5E' }}>Logistics &amp; Deliveries</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-gray-500">Control Tower · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
              <span className="text-gray-300 text-xs">·</span>
              <SupabaseStatusDot alertCount={totalAlerts} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Clock In/Out */}
            <button
              onClick={() => setClockedIn(p => !p)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 select-none"
              style={{
                background: clockedIn ? '#1C2E5E' : '#E89A3C',
                color: '#fff',
                minHeight: 40,
                boxShadow: '0 2px 8px rgba(232,154,60,0.3)'
              }}
            >
              <Icon name={clockedIn ? 'ClockIcon' : 'PlayCircleIcon'} size={16} />
              {clockedIn ? 'Clock Out' : 'Clock In'}
            </button>
            {/* Messenger */}
            <button className="relative w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
              <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-gray-600" />
            </button>
            {/* Urgent Alerts Bell */}
            <button
              className="relative w-10 h-10 rounded-full flex items-center justify-center transition-colors"
              style={{ background: totalAlerts > 0 ? '#fee2e2' : '#f3f4f6' }}
              onClick={() => setAlertsBellPulse(false)}
            >
              <Icon
                name="BellAlertIcon"
                size={20}
                className={totalAlerts > 0 ? 'text-red-600' : 'text-gray-500'}
                style={alertsBellPulse && totalAlerts > 0 ? { animation: 'pulse 1.5s infinite' } : {}}
              />
              {totalAlerts > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {totalAlerts}
                </span>
              )}
            </button>
            {/* Avatar */}
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
              {currentUser.first_name.charAt(0)}{currentUser.last_name.charAt(0)}
            </div>
          </div>
        </div>
        {/* AI Summary Bar */}
        <AISummaryBar
          pageLabel="Logistics"
          data={jobs.map(j => ({
            job_id: j.job_id,
            address: j.address,
            installation_date: j.installation_date,
            materials_status: j.materials_status,
            dumpster_status: j.dumpster_status,
            permit_status: j.permit_status,
            readiness_score: j.readiness_score,
            risk_flag: j.risk_flag,
            alerts: j.alerts,
          }))}
          systemPrompt="You are a logistics operations assistant for a roofing company. Summarise the current state of the logistics jobs in 1-2 sentences. Focus on: total jobs, any at-risk jobs, materials/dumpster delivery statuses, and any urgent alerts. Be concise and actionable."
        />
      </div>

      <div className="px-6 py-6 max-w-[1400px] mx-auto">

        <KpiStrip
          jobs={jobs}
          onAtRiskClick={() => {
            setFilter('at_risk');
            requestAnimationFrame(() => {
              const el = document.getElementById('logistics-queue-section');
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          }}
        />

        {/* ── Control Tower Widgets ─────────────────────────────────────── */}

        {/* Row 1: Full-width Live Radar Map */}
        <div className="mb-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" style={{ borderRadius: 12 }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#fee2e2' }}>
                  <Icon name="CloudIcon" size={18} className="text-red-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-sm" style={{ color: '#1C2E5E' }}>Live Weather Radar &amp; Job Site Map</h2>
                  <p className="text-[11px] text-gray-400 mt-0.5">Animated precipitation radar · RainViewer · All 6 job sites plotted</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {weatherAlerts.length > 0 && (
                  <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                    {weatherAlerts.length} Weather Alert{weatherAlerts.length > 1 ? 's' : ''}
                  </span>
                )}
                <span className="text-[10px] font-semibold text-[#E89A3C] bg-blue-50 px-2 py-0.5 rounded-full animate-pulse">LIVE</span>
              </div>
            </div>

            {/* Radar map — full width, tall */}
            <div style={{ height: 420 }}>
<LiveRadarMap
                jobs={jobs.map(j => ({
                  id: j.id,
                  job_id: j.job_id,
                  address: j.address,
                  lat: j.latitude ?? DEFAULT_COORDS.lat,
                  lng: j.longitude ?? DEFAULT_COORDS.lng,
                  weather_clear: j.weather_clear,
                  weather_message: j.alerts?.find(a => a.type === 'weather')?.message,
                  installation_date: j.installation_date,
                  materials_status: j.materials_status,
                  permit_status: j.permit_status,
                  risk_flag: j.risk_flag,
                }))}
              />
            </div>

            {/* Weather alert list below map */}
            {weatherAlerts.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-2">
                {weatherAlerts.map(job => (
                  <div key={job.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100">
                    <span className="text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full mt-0.5 flex-shrink-0">HIGH RISK</span>
                    <div>
                      <span className="text-xs font-semibold text-red-700">{job.job_id}</span>
                      <p className="text-[11px] text-red-600 leading-tight mt-0.5">
                        {job.alerts.find(a => a.type === 'weather')?.message}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Supplier & Delay Tracker + Delivery Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">

          {/* Widget 2: Supplier & Delay Tracker */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" style={{ borderRadius: 12 }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#fff7ed' }}>
                  <Icon name="TruckIcon" size={18} className="text-orange-600" />
                </div>
                <h2 className="font-semibold text-sm" style={{ color: '#1C2E5E' }}>Supplier &amp; Delay Tracker</h2>
              </div>
              {supplyAlerts.length > 0 && (
                <span className="text-[10px] font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                  {supplyAlerts.length} Issue{supplyAlerts.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {supplyAlerts.length === 0 ? (
                <div className="flex items-center gap-2 py-3">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-xs text-gray-500">All materials on schedule — no backorders</span>
                </div>
              ) : supplyAlerts.map(job => (
                job.alerts.filter(a => a.type === 'supply').map((alert, i) => {
                  const installDate = safeParseDate(job.installation_date);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const daysUntilInstall = installDate
                    ? Math.round((installDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                    : 0;
                  return (
                    <div key={`${job.id}-${i}`} className="flex items-start gap-2 p-2.5 rounded-lg bg-orange-50 border border-orange-100">
                      <span className="text-[10px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full mt-0.5 flex-shrink-0">BACKORDERED</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-orange-700 font-mono">{job.job_id}</span>
                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            {daysUntilInstall >= 0 ? `${daysUntilInstall} days to install` : `${Math.abs(daysUntilInstall)}d overdue`}
                          </span>
                        </div>
                        <p className="text-[11px] text-orange-600 leading-tight mt-0.5">{alert.message}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setResolveModalJob({ job, alert }); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-white border border-orange-200 text-orange-700 hover:bg-orange-100 transition-colors"
                          >
                            Resolve
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toast.success(`VPM notified for ${job.job_id}`); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            Notify VPM
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ))}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Dumpster Status</p>
                {[
                  ...jobs.filter(j => j.dumpster_status === 'Scheduled').slice(0, 3),
                  ...jobs.filter(j => j.dumpster_status === 'Placed'),
                ].map(job => {
                  const dotColor = job.dumpster_status === 'Scheduled' ? 'bg-blue-500' : 'bg-green-500';
                  const statusChipBg = job.dumpster_status === 'Scheduled' ? 'bg-blue-50' : job.dumpster_status === 'Placed' ? 'bg-amber-50' : 'bg-gray-100';
                  const statusChipText = job.dumpster_status === 'Scheduled' ? 'text-blue-700' : job.dumpster_status === 'Placed' ? 'text-amber-700' : 'text-gray-600';
                  return (
                    <div key={job.id} className="flex items-center justify-between py-1.5 gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
                        <span className="text-xs font-semibold text-gray-700 font-mono flex-shrink-0">{job.job_id}</span>
                        <span className="text-[11px] text-gray-500 truncate" title={`${job.dumpster_info?.vendor ?? ''} · ${job.dumpster_info?.size ?? ''}`}>
                          {job.dumpster_info?.vendor ?? ''} · {job.dumpster_info?.size ?? ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {job.dumpster_status === 'Scheduled' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDumpsterAction({ type: 'drop', job }); }}
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            aria-label={`Schedule dumpster drop for ${job.job_id}`}
                          >
                            + Drop
                          </button>
                        )}
                        {job.dumpster_status === 'Placed' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDumpsterAction({ type: 'pickup', job }); }}
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                            aria-label={`Schedule dumpster pickup for ${job.job_id}`}
                          >
                            ✓ Pickup
                          </button>
                        )}
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusChipBg} ${statusChipText}`}
                          style={{ background: '#1C2E5E' }}>
                          {job.dumpster_status === 'Scheduled' ? 'Drop Pending' : job.dumpster_status === 'Placed' ? 'On Site' : 'Removed'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Widget 3: Delivery Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" style={{ borderRadius: 12 }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#eff6ff' }}>
                  <Icon name="CalendarDaysIcon" size={18} className="text-[#1C2E5E]" />
                </div>
                <h2 className="font-semibold text-sm" style={{ color: '#1C2E5E' }}>Today&apos;s Delivery Timeline</h2>
              </div>
              <span className="text-[11px] text-gray-400">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
            <div className="space-y-1.5">
              {jobs.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No deliveries scheduled today</p>
              ) : (
                jobs.slice(0, 7).map((job) => (
                  <div key={job.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="text-[11px] font-mono text-gray-500 w-16 flex-shrink-0">
                      {job.installation_date ? new Date(job.installation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </span>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: job.materials_status === 'Delivered' ? '#16a34a' : '#E89A3C' }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-gray-800 truncate block">{job.materials_status}</span>
                    </div>
                    {job.materials_status !== 'Delivered' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkDelivered(job);
                        }}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 hover:bg-green-100 transition-colors flex-shrink-0"
                        aria-label={`Mark ${job.job_id} as delivered`}
                      >
                        ✓ Mark Delivered
                      </button>
                    )}
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
                      style={{ background: '#1C2E5E' }}>
                      {job.job_id}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Logistics Job Cards ───────────────────────────────────────── */}
        <div id="logistics-queue-section" className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold" style={{ color: '#1C2E5E' }}>
            Logistics Queue
            <span className="ml-2 text-sm font-normal text-gray-400">({processedJobs.length === jobs.length
              ? `${jobs.length} jobs`
              : `Showing ${processedJobs.length} of ${jobs.length} jobs`} · sorted by install date)</span>
          </h2>
        </div>

        <QueueControls
          activeFilter={filter}
          onFilterChange={setFilter}
          sortKey={sortKey}
          onSortChange={setSortKey}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          counts={filterCounts}
          totalJobs={jobs.length}
          visibleJobs={processedJobs.length}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {processedJobs.map(job => {
            const matCfg  = getMaterialsConfig(job.materials_status);
            const dumpCfg = getDumpsterConfig(job.dumpster_status);
            const hasAlerts = (job.alerts?.length ?? 0) > 0;

            return (
              <div
                key={job.id}
                onClick={() => openDrawer(job)}
                className="relative bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer overflow-hidden"
                style={{ borderRadius: 12 }}
              >
                {/* Card top bar */}
                <div className="px-4 pt-4 pb-3 border-b border-gray-50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <ReadinessRing score={job.readiness_score ?? 0} size={32} />
                        <span className="font-mono font-bold text-[#1C2E5E]">{job.job_id}</span>
                        {hasAlerts && (
                          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Active alerts" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5 leading-tight">{job.address}</p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      {job.risk_flag && (
                        <div className="flex justify-end mb-1">
                          <span
                            role="status"
                            title="This job has one or more red blockers"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-red-600 text-white shadow-sm"
                          >
                            <span className="w-1 h-1 rounded-full bg-white" aria-hidden="true" />
                            At Risk
                          </span>
                        </div>
                      )}
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">Install Date</p>
                      <p className="text-xs font-semibold" style={{ color: '#1C2E5E' }}>{formatDate(job.installation_date)}</p>
                      <div className="mt-1">
                        <SlaCountdown installDate={job.installation_date} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status badges */}
                <div className="px-4 py-3 flex flex-wrap gap-2">
                  {/* Materials */}
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${matCfg.bg} ${matCfg.text}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${matCfg.dot}`} />
                    Materials: {job.materials_status}
                  </div>
                  {/* Permit chip — after materials */}
                  <PermitChip status={job.permit_status ?? 'not_started'} />
                  {/* Dumpster */}
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${dumpCfg.bg} ${dumpCfg.text}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${dumpCfg.dot}`} />
                    Dumpster: {job.dumpster_status}
                  </div>
                  {/* Weather Clear */}
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${job.weather_clear ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${job.weather_clear ? 'bg-green-500' : 'bg-red-500'}`} />
                    Weather: {job.weather_clear ? 'Clear' : 'Warning'}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 pb-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-[11px] text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-[9px] font-bold text-[#1C2E5E]"
                        aria-label={job.vpm_name ?? job.contact_directory?.pm?.name ?? ''}
                      >
                        {getInitials(job.vpm_name ?? job.contact_directory?.pm?.name ?? '')}
                      </span>
                      <span className="truncate max-w-[80px]" title={job.vpm_name ?? job.contact_directory?.pm?.name ?? ''}>
                        {job.vpm_name ?? job.contact_directory?.pm?.name ?? 'Unassigned'}
                      </span>
                    </div>
                    {job.lead_installer_name && (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-[9px] font-bold text-orange-700"
                          aria-label={job.lead_installer_name}
                        >
                          {getInitials(job.lead_installer_name)}
                        </span>
                        <span className="truncate max-w-[80px]" title={job.lead_installer_name}>
                          {job.lead_installer_name}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-[#E89A3C]">
                    <span className="text-[11px] font-medium">View Details</span>
                    <Icon name="ChevronRightIcon" size={14} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {processedJobs.length === 0 && jobs.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><path d="m21 21-3.5-3.5" strokeLinecap="round" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-700">No jobs match your filters</p>
            <p className="text-[12px] text-gray-500 mt-1">
              {searchQuery ? `No results for "${searchQuery}"` : `Try a different filter or clear your search`}
            </p>
            <button
              type="button"
              onClick={() => { setFilter('all'); setSearchQuery(''); }}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-[#E89A3C] text-white hover:bg-[#0891c9] transition-colors"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* ── Slide-out Drawer ──────────────────────────────────────────────── */}
      {/* Backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-300"
        style={{ background: 'rgba(0,0,0,0.45)', zIndex: 9998, opacity: drawerOpen ? 1 : 0, pointerEvents: drawerOpen ? 'auto' : 'none' }}
        onClick={closeDrawer}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full bg-white shadow-2xl transition-transform duration-300 ease-in-out overflow-y-auto"
        style={{
          width: 'min(680px, 95vw)',
          zIndex: 9999,
          transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {selectedJob && (
          <>
            {/* Drawer Header */}
            <div className="sticky top-0 z-10 px-6 py-5 border-b border-gray-100 bg-white">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-bold" style={{ color: '#1C2E5E' }}>{selectedJob.job_id}</span>
                    {(selectedJob.alerts?.length ?? 0) > 0 && (
                      <span className="text-[10px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                        {selectedJob.alerts.length} ALERT{selectedJob.alerts.length > 1 ? 'S' : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{selectedJob.address}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Install: {formatDate(selectedJob.installation_date)}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button className="px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-colors hover:bg-blue-50"
                    style={{ borderColor: '#1C2E5E', color: '#1C2E5E' }}>
                    Modify Schedule
                  </button>
                  <button
                    onClick={() => { setEscalateDesc(''); setEscalateDescModal({ type: 'drawer', job: selectedJob }); }}
                    disabled={drawerEscalateLoading}
                    className="px-3 py-2 rounded-lg text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    {drawerEscalateLoading ? (
                      <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    ) : null}
                    {drawerEscalateLoading ? 'Escalating…' : 'Escalate Issue'}
                  </button>
                  <button onClick={closeDrawer} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors ml-1">
                    <Icon name="XMarkIcon" size={16} className="text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 space-y-6">

              {/* Active Alerts */}
              {(selectedJob.alerts?.length ?? 0) > 0 && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">Active Alerts</h3>
                  {selectedJob.alerts.map((alert, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${alert.type === 'weather' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                      <Icon name={alert.type === 'weather' ? 'CloudIcon' : 'ExclamationTriangleIcon'} size={16}
                        className={alert.type === 'weather' ? 'text-red-600 mt-0.5' : 'text-orange-600 mt-0.5'} />
                      <p className={`text-xs leading-relaxed ${alert.type === 'weather' ? 'text-red-700' : 'text-orange-700'}`}>{alert.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Contact Directory */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Contact Directory</h3>
                <div className="grid grid-cols-2 gap-3">
                  {/* PM */}
                  <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-[#1C2E5E]">Project Manager</span>
                      <button className="w-6 h-6 rounded-full bg-[#E89A3C]/10 flex items-center justify-center hover:bg-[#E89A3C]/20 transition-colors">
                        <Icon name="ChatBubbleLeftEllipsisIcon" size={12} className="text-[#E89A3C]" />
                      </button>
                    </div>
                    <p className="text-xs font-semibold text-gray-800">{selectedJob.contact_directory?.pm?.name}</p>
                    {selectedJob.contact_directory?.pm?.phone && (
                      <p className="text-[11px] text-gray-500 mt-0.5">{selectedJob.contact_directory.pm.phone}</p>
                    )}
                  </div>
                  {/* Foreman */}
                  <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-[#1C2E5E]">Crew Foreman</span>
                      <a href={`tel:${selectedJob.contact_directory?.foreman?.phone}`}
                        className="w-6 h-6 rounded-full bg-green-50 flex items-center justify-center hover:bg-green-100 transition-colors">
                        <Icon name="PhoneIcon" size={12} className="text-green-600" />
                      </a>
                    </div>
                    <p className="text-xs font-semibold text-gray-800">{selectedJob.contact_directory?.foreman?.name}</p>
                    {selectedJob.contact_directory?.foreman?.phone && (
                      <p className="text-[11px] text-gray-500 mt-0.5">{selectedJob.contact_directory.foreman.phone}</p>
                    )}
                    {selectedJob.contact_directory?.foreman?.note && (
                      <p className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded mt-1.5 leading-tight">
                        📝 {selectedJob.contact_directory.foreman.note}
                      </p>
                    )}
                  </div>
                  {/* Homeowner */}
                  <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-[#1C2E5E]">Homeowner</span>
                      <a href={`tel:${selectedJob.contact_directory?.homeowner?.phone}`}
                        className="w-6 h-6 rounded-full bg-green-50 flex items-center justify-center hover:bg-green-100 transition-colors">
                        <Icon name="PhoneIcon" size={12} className="text-green-600" />
                      </a>
                    </div>
                    <p className="text-xs font-semibold text-gray-800">{selectedJob.contact_directory?.homeowner?.name}</p>
                    {selectedJob.contact_directory?.homeowner?.phone && (
                      <p className="text-[11px] text-gray-500 mt-0.5">{selectedJob.contact_directory.homeowner.phone}</p>
                    )}
                    {selectedJob.contact_directory?.homeowner?.note && (
                      <p className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded mt-1.5 leading-tight">
                        📝 {selectedJob.contact_directory.homeowner.note}
                      </p>
                    )}
                  </div>
                  {/* Supplier */}
                  <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-[#1C2E5E]">Supplier / Driver</span>
                      <a href={`tel:${selectedJob.contact_directory?.supplier?.phone}`}
                        className="w-6 h-6 rounded-full bg-green-50 flex items-center justify-center hover:bg-green-100 transition-colors">
                        <Icon name="PhoneIcon" size={12} className="text-green-600" />
                      </a>
                    </div>
                    <p className="text-xs font-semibold text-gray-800">{selectedJob.contact_directory?.supplier?.name}</p>
                    {selectedJob.contact_directory?.supplier?.company && (
                      <p className="text-[11px] text-[#E89A3C] font-medium">{selectedJob.contact_directory.supplier.company}</p>
                    )}
                    {selectedJob.contact_directory?.supplier?.phone && (
                      <p className="text-[11px] text-gray-500 mt-0.5">{selectedJob.contact_directory.supplier.phone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Material Order Breakdown */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Material Order Breakdown</h3>
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Item</th>
                        <th className="text-center px-3 py-2.5 font-semibold text-gray-600">Qty</th>
                        <th className="text-center px-3 py-2.5 font-semibold text-gray-600">Status</th>
                        <th className="text-center px-3 py-2.5 font-semibold text-gray-600">Track</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedJob.material_order?.map((item, idx) => {
                        const key = `${selectedJob.id}-${idx}`;
                        const currentStatus = materialStatuses[key] ?? item.status;
                        const cfg = getMaterialItemConfig(currentStatus);
                        return (
                          <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                            <td className="px-3 py-2.5 text-gray-800 leading-tight">{item.item}</td>
                            <td className="px-3 py-2.5 text-center text-gray-600 font-mono">{item.qty}</td>
                            <td className="px-3 py-2.5 text-center">
                              <select
                                value={currentStatus}
                                onChange={e => setMaterialStatuses(prev => ({ ...prev, [key]: e.target.value }))}
                                className={`text-[11px] font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${cfg.bg} ${cfg.text}`}
                              >
                                {['Pending', 'Ordered', 'Loaded', 'Delivered'].map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              {item.tracking ? (
                                <a href="#" className="text-[#E89A3C] hover:underline font-medium text-[11px]">Track →</a>
                              ) : (
                                <span className="text-gray-300 text-[11px]">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Asset Timeline Stepper */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Asset Timeline</h3>
                <div className="relative pl-6">
                  {/* Vertical line */}
                  <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-gray-200" />

                  {selectedJob.asset_timeline?.map((step, idx) => {
                    const isLast = idx === (selectedJob.asset_timeline?.length ?? 0) - 1;
                    const iconMap: Record<string, string> = {
                      truck: 'TruckIcon',
                      package: 'ArchiveBoxIcon',
                      users: 'UsersIcon',
                      trash: 'TrashIcon'
                    };
                    const iconName = iconMap[step.icon] ?? 'CheckCircleIcon';
                    const colorMap: Record<string, string> = {
                      truck: '#1C2E5E',
                      package: '#E89A3C',
                      users: '#16a34a',
                      trash: '#dc2626'
                    };
                    const color = colorMap[step.icon] ?? '#6b7280';

                    return (
                      <div key={idx} className={`relative flex items-start gap-4 ${!isLast ? 'mb-5' : ''}`}>
                        {/* Step dot */}
                        <div className="absolute -left-6 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 bg-white"
                          style={{ borderColor: step.completed ? '#16a34a' : color }}>
                          {step.completed ? (
                            <Icon name="CheckIcon" size={10} className="text-green-600" />
                          ) : (
                            <div className="w-2 h-2 rounded-full" style={{ background: color }} />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 pb-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Icon name={iconName as any} size={14} style={{ color }} />
                              <span className={`text-sm font-semibold ${step.completed ? 'text-green-700 line-through' : ''}`}
                                style={!step.completed ? { color: '#1C2E5E' } : {}}>
                                {step.step}
                              </span>
                              {step.completed && (
                                <span className="text-[10px] font-semibold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">DONE</span>
                              )}
                            </div>
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={step.completed}
                              readOnly
                              className="w-4 h-4 rounded accent-green-600 cursor-pointer"
                            />
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5 ml-5">ETA: {step.eta}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Permit section */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Permit</h3>
                {(() => {
                  const existingPermit = permitsByJobId[selectedJob.job_id];
                  if (existingPermit) {
                    return (
                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Jurisdiction</p>
                            <p className="text-[13px] font-semibold text-[#1C2E5E] truncate">{existingPermit.jurisdiction_name ?? 'Not identified'}</p>
                          </div>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 flex-shrink-0">
                            {(existingPermit.status ?? '').replace(/_/g, ' ')}
                          </span>
                        </div>
                        {existingPermit.permit_portal_url && (
                          <a
                            href={existingPermit.permit_portal_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-[#E89A3C] hover:underline truncate block mt-1"
                            title={existingPermit.permit_portal_url}
                          >
                            {existingPermit.permit_portal_url}
                          </a>
                        )}
                        <Link
                          href="/permits"
                          className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-[#1C2E5E] hover:underline"
                        >
                          Open in Permits →
                        </Link>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      <p className="text-[11px] text-gray-600">
                        No permit started yet. Use AI to identify the correct jurisdiction for this address.
                      </p>
                      <IdentifyJurisdictionButton
                        address={selectedJob.address}
                        variant="primary"
                        label="Create Permit with AI"
                        disabled={false}
                        onSuccess={(result) => handleCreatePermitFromAI(selectedJob, result)}
                        onError={(err) => toast.error(`Jurisdiction identification failed: ${err instanceof Error ? err.message : 'Unknown error'}`)}
                      />
                    </div>
                  );
                })()}
              </div>

            </div>
          </>
        )}

      </div>
      {/* End of Drawer panel */}

      {/* ── Supplier Resolve Modal ──────────────────────────────────────────── */}
      {resolveModalJob && (() => {
        const { job, alert } = resolveModalJob;
        // Find the index of this alert in the job's alerts array so we can remove it on resolve
        const alertIndex = (job.alerts ?? []).findIndex(a => a.type === alert.type && a.message === alert.message);
        // Build the alertRow object passed to each handler
        const alertRow: any = {
          job_id: job.job_id,
          jobUUID: job.id,
          job_address: job.address,
          supplier_name: job.contact_directory?.supplier?.name ?? 'Supplier',
          material_description: alert.message ?? '',
          notes: null,
          expected_delivery_date: job.installation_date ?? '',
          // Pass the full alerts array + index so handlers can splice out the resolved alert
          currentAlerts: job.alerts ?? [],
          alertIndex: alertIndex >= 0 ? alertIndex : 0,
        };
        return (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            style={{ zIndex: 10000 }}
            onClick={() => { if (!resolveActionLoading) setResolveModalJob(null); }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="resolve-modal-title"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 id="resolve-modal-title" className="text-base font-bold text-[#1C2E5E]">Resolve Supply Alert</h3>
                    <p className="text-[11px] text-gray-500 mt-0.5 font-mono">{job.job_id}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setResolveModalJob(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close modal"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Alert info */}
              <div className="px-5 pt-4 pb-2">
                <div className="rounded-lg bg-orange-50 border border-orange-100 p-3 mb-4">
                  <p className="text-[12px] text-orange-700 leading-relaxed">{alert.message}</p>
                </div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Choose a resolution action</p>
              </div>

              {/* Action buttons */}
              <div className="px-5 pb-2 space-y-2">
                {/* Substitute SKU */}
                <button
                  type="button"
                  onClick={() => handleSubstituteSKU(alertRow)}
                  disabled={resolveActionLoading !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {resolveActionLoading === 'substitute' ? (
                      <svg className="w-4 h-4 animate-spin text-blue-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 0 0 4.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 0 1-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800">{resolveActionLoading === 'substitute' ? 'Saving…' : 'Substitute SKU'}</p>
                    <p className="text-[11px] text-gray-500">Replace with an equivalent available material</p>
                  </div>
                </button>

                {/* Rush Ship */}
                <button
                  type="button"
                  onClick={() => handleRushShip(alertRow)}
                  disabled={resolveActionLoading !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-amber-50 hover:border-amber-200 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    {resolveActionLoading === 'rush' ? (
                      <svg className="w-4 h-4 animate-spin text-amber-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="m3.27 6.96 8.73 5.05 8.73-5.05M12 22.08V12" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800">{resolveActionLoading === 'rush' ? 'Saving…' : 'Rush Ship'}</p>
                    <p className="text-[11px] text-gray-500">Request expedited delivery from supplier</p>
                  </div>
                </button>

                {/* Split Ship */}
                <button
                  type="button"
                  onClick={() => handleSplitShip(alertRow)}
                  disabled={resolveActionLoading !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-purple-50 hover:border-purple-200 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    {resolveActionLoading === 'split' ? (
                      <svg className="w-4 h-4 animate-spin text-purple-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="m3.27 6.96 8.73 5.05 8.73-5.05M12 22.08V12" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800">{resolveActionLoading === 'split' ? 'Saving…' : 'Split Ship'}</p>
                    <p className="text-[11px] text-gray-500">Partial delivery now, remainder to follow</p>
                  </div>
                </button>

                {/* Contact Supplier */}
                <button
                  type="button"
                  onClick={() => handleContactSupplier(alertRow)}
                  disabled={resolveActionLoading !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-green-50 hover:border-green-200 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    {resolveActionLoading === 'contact' ? (
                      <svg className="w-4 h-4 animate-spin text-green-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800">{resolveActionLoading === 'contact' ? 'Saving…' : 'Contact Supplier'}</p>
                    <p className="text-[11px] text-gray-500">Log outreach to supplier — no status change</p>
                  </div>
                </button>

                {/* Escalate */}
                <button
                  type="button"
                  onClick={() => { setEscalateDesc(''); setEscalateDescModal({ type: 'alert', alert: alertRow }); }}
                  disabled={resolveActionLoading !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    {resolveActionLoading === 'escalate' ? (
                      <svg className="w-4 h-4 animate-spin text-red-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4m0 4h.01" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-red-700">{resolveActionLoading === 'escalate' ? 'Escalating…' : 'Escalate'}</p>
                    <p className="text-[11px] text-red-500">Create escalation ticket + flag alert in Operations</p>
                  </div>
                </button>
              </div>

              {/* Footer */}
              <div className="flex justify-end p-5 pt-3">
                <button
                  type="button"
                  onClick={() => setResolveModalJob(null)}
                  className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <DumpsterConfirmDialog
        action={dumpsterAction}
        onClose={() => setDumpsterAction(null)}
      />
      <LogIncidentModal
        isOpen={logIncidentOpen}
        onClose={() => setLogIncidentOpen(false)}
        jobs={jobs}
      />
      <CreateJobModal
        isOpen={createJobOpen}
        onClose={() => setCreateJobOpen(false)}
        onCreated={() => { loadJobs(); setCreateJobOpen(false); }}
      />
      <ScheduleInspectionModal
        isOpen={scheduleInspectionOpen}
        onClose={() => setScheduleInspectionOpen(false)}
        jobs={jobs}
        preselectedJobId={null}
        onScheduled={() => {}}
      />
      <QuickActionsFab
        onLogIncident={() => setLogIncidentOpen(true)}
        onCreateJob={() => setCreateJobOpen(true)}
        onScheduleInspection={() => setScheduleInspectionOpen(true)}
        onSendBroadcast={() => {}}
        onAddMaterialOrder={() => {}}
      />

      {/* Escalation Description Modal */}
      {escalateDescModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="ExclamationTriangleIcon" size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-gray-900">Describe the Problem</h3>
                <p className="text-[12px] text-gray-500">
                  {escalateDescModal.type === 'drawer'
                    ? `Job ${escalateDescModal.job.job_id}`
                    : `Alert — ${escalateDescModal.alert.job_id}`}
                </p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                  Problem Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Describe the issue in detail so the team understands the problem…"
                  value={escalateDesc}
                  onChange={e => setEscalateDesc(e.target.value)}
                  autoFocus
                />
                <p className="text-[11px] text-gray-400 mt-1.5">This will be included at the top of the escalation ticket.</p>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                  Escalate To <span className="text-red-500">*</span>
                  <span className="ml-1 text-[11px] font-normal text-gray-400">(select one or more departments)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['Logistics', 'Quality Control', 'Billing', 'Finance', 'Operations', 'Executive'].map(dept => {
                    const selected = escalateDepts.includes(dept);
                    return (
                      <button
                        key={dept}
                        type="button"
                        onClick={() => setEscalateDepts(prev =>
                          selected ? prev.filter(d => d !== dept) : [...prev, dept]
                        )}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[12px] font-semibold transition-all text-left ${
                          selected
                            ? 'border-red-500 bg-red-50 text-red-700' :'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                          selected ? 'bg-red-500 border-red-500' : 'border-gray-300 bg-white'
                        }`}>
                          {selected && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        {dept}
                      </button>
                    );
                  })}
                </div>
                {escalateDepts.length > 0 && (
                  <p className="text-[11px] text-red-600 mt-2 font-medium">
                    Will create {escalateDepts.length} ticket{escalateDepts.length > 1 ? 's' : ''}: {escalateDepts.join(', ')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={() => { setEscalateDescModal(null); setEscalateDepts([]); }}
                className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!escalateDesc.trim() || escalateDepts.length === 0}
                onClick={() => {
                  const desc = escalateDesc.trim();
                  const depts = [...escalateDepts];
                  if (escalateDescModal.type === 'alert') {
                    setEscalateDescModal(null);
                    setEscalateDepts([]);
                    handleEscalate(escalateDescModal.alert, desc, depts);
                  } else {
                    setEscalateDescModal(null);
                    setEscalateDepts([]);
                    handleDrawerEscalate(escalateDescModal.job, desc, depts);
                  }
                }}
                className="px-5 py-2 rounded-lg text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Escalation
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


